import * as React from "react";
import { useCallback, useMemo } from "react";

import { Slider } from "@/components/ui/slider";
import { componentMilesSliderTestIds } from "@/lib/data-test-ids/components.data-test-ids";
import { cn } from "@/lib/utils";

interface MilesSliderProps {
  value: number;
  onChange: (value: number) => void;
  showValue?: boolean;
  showLabels?: boolean;
  className?: string;
  milesRanges: readonly number[];
}

interface MilesSliderViewProps {
  sliderValue: number[];
  onValueChange: (value: number[]) => void;
  max: number;
  displayValue: number;
  showValue: boolean;
  showLabels: boolean;
  className?: string;
  milesRanges: readonly number[];
}

function findClosestIndex(value: number, ranges: readonly number[]): number {
  const exactIndex = ranges.indexOf(value);
  if (exactIndex !== -1) return exactIndex;

  let closestIndex = 0;
  const firstRange = ranges[0];
  if (firstRange === undefined) return 0;
  let closestDistance = Math.abs(firstRange - value);

  for (let i = 1; i < ranges.length; i++) {
    const rangeValue = ranges[i];
    if (rangeValue === undefined) continue;
    const distance = Math.abs(rangeValue - value);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = i;
    }
  }

  return closestIndex;
}

function MilesSliderView({
  sliderValue,
  onValueChange,
  max,
  displayValue,
  showValue,
  showLabels,
  className,
  milesRanges,
}: MilesSliderViewProps) {
  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        <Slider
          value={sliderValue}
          onValueChange={onValueChange}
          max={max}
          min={0}
          step={1}
          className={cn(
            "[&_[data-slot=slider-range]]:bg-ink-subtle [&_[data-slot=slider-thumb]]:border-ink-subtle [&_[data-slot=slider-thumb]]:ring-ink-subtle/50",
            className,
          )}
          data-testid={componentMilesSliderTestIds.SLIDER_TRIGGER_ELEMENT}
        />
        {showValue && (
          <span data-testid={componentMilesSliderTestIds.SLIDER_VALUE_ELEMENT}>
            {displayValue}
          </span>
        )}
      </div>
      {showLabels && (
        <div className="relative w-full text-[10px] text-ink-subtle h-4 mt-1">
          {milesRanges.map((labelValue, index) => {
            const fraction =
              milesRanges.length > 1 ? index / (milesRanges.length - 1) : 0;
            const isActive = labelValue === displayValue;
            return (
              <span
                key={labelValue}
                data-isactive={isActive}
                className={cn(
                  "absolute -translate-x-1/2",
                  isActive ? "font-semibold text-foreground" : "",
                )}
                style={{
                  left: `calc(0.5rem + (100% - 1rem) * ${fraction})`,
                }}
              >
                {labelValue}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MilesSlider({
  value,
  onChange,
  showValue = true,
  showLabels = true,
  className,
  milesRanges,
}: MilesSliderProps) {
  const currentIndex = useMemo(
    () => findClosestIndex(value, milesRanges),
    [value, milesRanges],
  );

  const handleValueChange = useCallback(
    (newValue: number[]) => {
      const index = newValue[0];
      if (index === undefined) return;

      const milesValue = milesRanges[index];
      if (milesValue === undefined) return;

      if (milesValue === value) return;

      onChange(milesValue);
    },
    [value, onChange, milesRanges],
  );

  return (
    <MilesSliderView
      sliderValue={[currentIndex]}
      onValueChange={handleValueChange}
      max={milesRanges.length - 1}
      displayValue={value}
      showValue={showValue}
      showLabels={showLabels}
      className={className}
      milesRanges={milesRanges}
    />
  );
}
